import 'maxkir/common/listeners';

interface LocationsModel extends ShiftShiftModel {
    getFoundLists(): ListData[];
    getFoundListItems(): TaskData[];
    isLoadingItem(list_id: number, task_id: number): boolean;

    getHeader(): string|null;
    setSelectionInfo(list_id: number, task_id: number, selectionId: number): void;
    backState(): number; // Return the selection ID of the item to select
    forwardState(): void;
}

let _current_text = null;
let _delayed_typing = null;
let _listeners = new maxkir.Listeners();


let _items_provider: maxkir_ListItemSearcher = null;
const MAX_COMPLETION_ITEMS = 100;


const itemsProvider = () => {
    if (!_items_provider) {
        _items_provider = new maxkir.ListItemSearcher(MAX_COMPLETION_ITEMS);
        _items_provider.setHasMoreDataCallback(() => {
            _listeners.notify_listeners_as_function();
        });
    }
    return _items_provider;
}

const setCurrentTextAndRepaint = () => {
    itemsProvider().setSearchedText(_current_text, MAX_COMPLETION_ITEMS);
    _listeners.notify_listeners_as_function();
};

const _states_stack = [];

// Cache for previously found data to show some real info while information is loaded from the
// server
let _old_items = [];
let _old_lists = [];
let _reset_selection: boolean = false;

const model: LocationsModel = {
    code: 'locations',

    getFoundListItems(): TaskData[] {
        const searcher = itemsProvider();
        if (!searcher.isNothingFound() || !searcher.isLoading()) {
            _old_items = searcher.getFoundListItems();
        }
        return _old_items;
    },

    getFoundLists(): ListData[] {
        const searcher = itemsProvider();
        if (!searcher.isNothingFound() || !searcher.isLoading()) {
            _old_lists = searcher.getFoundLists();
        }
        return _old_lists;
    },

    shouldResetSelection(): boolean {
        const res = _reset_selection;
        _reset_selection = false;
        return res;
    },

    isLoading() {
        return itemsProvider().isLoading();
    },

    isLoadingItem(list_id: number, task_id: number) {
        return itemsProvider().isLoadingItem(list_id || 0, task_id || 0);
    },

    onTextChanged(newText: string, delayProcessing: boolean) {
        _current_text = newText;
        if (!delayProcessing) {
            setCurrentTextAndRepaint();
            return;
        }

        if (!_delayed_typing) {
            _delayed_typing = maxkir.delay_action(() => {
                setCurrentTextAndRepaint();
            }, 150);
        }
        _delayed_typing.start();
    },

    getFilterTerms(): string[] {
        return !_current_text ? [] : _current_text.split(/ +/);
    },

    addDataAvailableCallback(callback) {
        _listeners.add_listener(callback)
    },

    removeDataAvailableCallback(callback) {
        _listeners.remove_listener(callback)
    },

    setSelectionInfo(list_id: number, task_id: number, selectionId: number) {
        task_id = task_id || (list_id ? 0 : null);

        if (this._selectionId === selectionId && (this._scope_list_id !== list_id || this._scope_task_id !== task_id)) {
            // Selected item on the same position has changed - reset selection to beginning
            _reset_selection = true;
        }

        this._scope_list_id = list_id;
        this._scope_task_id = task_id;
        this._selectionId = selectionId;

        maxkir.debug("Selection: " + this._scope_list_id + " " + this._scope_task_id);
    },

    getHeader() {
        const list_id = itemsProvider().getParentContext().list_id;
        if (list_id) {
            return maxkir.Lists.data(list_id).name;
        }
        return null;
    },

    forwardState() {
        const scope_task_id = this._scope_task_id;
        const data = maxkir.Task.data(scope_task_id);
        if (scope_task_id && (!data || !data.tasks || !data.tasks.length)) {
            return;
        }

        _states_stack.push({
            filter: _current_text,
            selectionId: this._selectionId,
            task_id: itemsProvider().getParentContext().task_id,
            list_id: itemsProvider().getParentContext().list_id
        });

        itemsProvider().setParentContext(this._scope_list_id, this._scope_task_id);
        _current_text = "";
        _reset_selection = true;
        setCurrentTextAndRepaint();
        maxkir.debug("forward");
    },

    backState() {
        const previousState = _states_stack.pop();
        if (previousState) {
            itemsProvider().setParentContext(previousState.list_id, previousState.task_id);
            _current_text = previousState.filter;
            setCurrentTextAndRepaint();
            maxkir.debug("back");
            return previousState.selectionId;
        }
        return -1;
    },

    dispose() {
        _listeners.dispose();
        _items_provider && _items_provider.stop();
        _items_provider = null
    }
}

export default model;